import React, {useEffect, useMemo} from 'react';

import type {
  RenderTreeNodePayload} from '@mantine/core';
import {Button, Modal, Tree} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import {useDisclosure} from '@mantine/hooks';
import {Textbox} from 'src/design/atoms/Textbox';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {useGetAssetQuery} from 'src/redux/queries/asset';
import {AssetTreeLeaf} from './AssetTreeComponents';
import {useAssetTreeContent} from './AssetTreeContent';
import classes from './AssetTreeSelector.module.css';

export interface AssetTreeSelectorProps {
  id?: number;
  rootAssetId?: number;
  onChange: (id?: number, text?: string) => void;
  includeLeaves: boolean;
  requireLeaf: boolean;

  buttonLabel?: string;
  modalLabel?: string;
  modalDescription?: string;
  disabled?: boolean;
  showFullPath?: boolean;
}
export const AssetTreeSelector: React.FC<AssetTreeSelectorProps> = ({
  id,
  rootAssetId,
  onChange,
  includeLeaves,
  requireLeaf,
  buttonLabel,
  modalLabel,
  modalDescription,
  disabled,
  showFullPath,
}) => {
  const [opened, {open, close}] = useDisclosure(false);

  const {
    searchTerm,
    handleSearchTerm,
    isLoading: isDataXLoading,
    filteredTreeNodeData,
    tree,
  } = useAssetTreeContent({
    treeQueryParams: {
      activeStates: [true],
      isLeaf: includeLeaves ? undefined : false,
      parentId: rootAssetId,
    },
    skipQuery: false,
    initialSelectedId: id ? String(id) : undefined,
    expandOnMount: true,
  });

  const {
    data: root,
    isLoading: isRootLoading,
    isFetching: isRootFetching,
  } = useGetAssetQuery(Number(rootAssetId), {skip: !rootAssetId});

  const {
    data: asset,
    isLoading: isAssetLoading,
    isFetching: isAssetFetching,
  } = useGetAssetQuery(Number(id), {skip: !id || isNaN(Number(id))});

  const isAssetLeaf = asset?.isLeaf === true;
  const isAssetLeafRequired = isAssetLeaf && requireLeaf && !id;

  const isRootXLoading = isRootLoading || isRootFetching;
  const isAssetXLoading = isAssetLoading || isAssetFetching;
  const isLoading = isDataXLoading || isRootXLoading || isAssetXLoading;

  const title = modalLabel ? modalLabel : root ? root?.name : 'Assets';
  const button = buttonLabel
    ? buttonLabel
    : root
    ? `Choose ${root?.name}`
    : 'Choose Asset';

  useEffect(() => {
    if (opened && id) {
      tree.select(String(id));
    } else if (opened) {
      tree.clearSelected();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, id]);

  const findNodeInTree = (
    id: string,
    nodes: typeof filteredTreeNodeData
  ): (typeof filteredTreeNodeData)[0] | undefined => {
    for (const node of nodes) {
      if (node.value === id) {
        return node;
      }
      if (node.children) {
        const found = findNodeInTree(id, node.children);
        if (found) {
          return found;
        }
      }
    }
    return undefined;
  };

  const handleConfirm = () => {
    const selectedId = tree.selectedState[0];
    if (!selectedId) {
      return;
    }
    const id = Number(selectedId);
    const node = findNodeInTree(selectedId, filteredTreeNodeData);
    const text = node?.label ? String(node.label) : undefined;
    onChange(id, text);
    close();
  };

  const renderText = useMemo(() => {
    if (showFullPath) {
      return asset?.path ?? 'None';
    }
    return asset?.name ?? 'None';
  }, [asset, showFullPath]);

  return (
    <>
      <Text size="sm" mb="xs">
        Selected: {isAssetXLoading ? 'Loading...' : renderText}
      </Text>
      <Button
        size="sm"
        color="black"
        variant="outline"
        onClick={open}
        disabled={isRootXLoading || disabled}>
        {isRootXLoading ? 'Loading...' : button}
      </Button>
      <Modal
        opened={opened}
        onClose={close}
        title={title}
        centered
        zIndex={9999}>
        <Modal.Header pos={'sticky'} style={{padding: 0}}>
          <Box w="100%">
            <Text size="xs" c="dimmed" mb="xs">
              {modalDescription ??
                `Select an asset from the options below. Selecting an asset
  that contains other asset will include all child assets.`}
            </Text>
            <Textbox
              label="Search"
              name="search"
              value={searchTerm}
              onChangeCallback={handleSearchTerm}
              autoComplete={false}
            />

            <Divider />
          </Box>
        </Modal.Header>

        {isLoading ? (
          <TableBodyRender isLoading={isLoading} />
        ) : (
          <Tree
            classNames={classes}
            selectOnClick
            data={filteredTreeNodeData}
            renderNode={(payload: RenderTreeNodePayload) => (
              <AssetTreeLeaf {...payload} />
            )}
            mb="md"
            tree={tree}
          />
        )}

        <Modal.Header pos={'sticky'} bottom={0} style={{padding: 0}}>
          <Group justify="end" w="100%" onClick={handleConfirm}>
            {isAssetLeafRequired && (
              <Text size="sm" c="dimmed">
                Please select a physical asset to continue.
              </Text>
            )}
            <Button
              disabled={tree.selectedState.length === 0 || isAssetLeafRequired}>
              Confirm
            </Button>
          </Group>
        </Modal.Header>
      </Modal>
    </>
  );
};
